import React, { useState, useEffect, useRef } from 'react';
import {
    BringzzAccordion,
    BringzzButton,
    BringzzPromoBanner,
    BringzzText,
    BringzzTopBar,
    BringzzBottomDrawer,
    BringzzRotatingSelect,
    BringzzBasketProduct,
    BringzzIcon,
    BringzzOrderFlow,
    BringzzPageContainer
} from '@bringzz/components';
import CheckoutAddressSelect from '../../components/CheckoutAddressSelect';
import CheckoutSummary from 'components/CheckoutSummary';
import AddLocation from 'components/AddLocation';
import { useNavigation } from '../../context/NavigationContext';
import useRequest from 'hooks/useRequest';
import BringzzCheckoutPicker from 'components/BringzzCheckoutPicker';
import BasketRecommendationSection from './BasketRecommendationSection';
import EmptyBasketState from './EmptyBasketState';
import { useBasket } from '../../context/BasketContext';
import { useAuth } from 'context/AuthContext';
import { useOrder } from 'context/OrderContext';
import AddressSelect from '../../components/AddressSelect';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import useHomepagePolling from 'hooks/useHomepagePolling';
import DefaultProductImage from 'assets/DefaultProductImage.png';

const BasketPage = () => {
    const { t } = useTranslation();
    const { navigate } = useNavigation();
    const { user, updateUserAddresses } = useAuth();
    const [userAddress, setUserAddress] = useState(null);
    
    const getFormattedAddress = (address) => {
        if (!address) return t('component.basket.address.select');
        const streetPart = [address.streetName, address.houseNumber].filter(Boolean).join(' ');
        const cityPart = [address.postalCode, address.cityName].filter(Boolean).join(' ');
        return [streetPart, cityPart].filter(Boolean).join(', ');
    };
    
    useEffect(() => {
        if (user?.activeAddress) {
            setUserAddress(getFormattedAddress(user.activeAddress));
        } else {
            setUserAddress(t('component.basket.address.select'));
        }
    }, [user?.activeAddress, t]);

    const { sendRequest, loading } = useRequest();
    const [showForWhomModal, setShowForWhomModal] = useState(false);
    const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);

    // Use the polling manager for notifications only (no active orders needed on basket page)
    useHomepagePolling({
        enabled: true,
        activeOrders: {
            enabled: false  // Disable active orders polling on basket page
        },
        notifications: {
            enabled: true,
            interval: 45000, // Poll every 45 seconds
            immediate: true,
            onSuccess: (data) => {
                setHasUnreadNotifications(data.hasUnread);
            }
        }
    });
    const { basketItems, removeFromBasket, updateQuantity, clearBasket, getBasketTotal, getBasketSubTotal, getDeliveryCost, getFees, forceRefreshBasket, getBasketForOrderType } =
        useBasket();
    const { prescriptionInfo, clearPrescriptionInfo } = useOrder();

    // Force re-render when prescriptionInfo changes (fixes context loading issue)
    useEffect(() => {
        // This effect ensures the component re-renders when prescriptionInfo loads from localStorage
    }, [prescriptionInfo]);
    const [addingToBasket, setAddingToBasket] = useState({});
    const [customAddress, setCustomAddress] = useState(null);
    const [pharmacies, setPharmacies] = useState([])
    const [showingDrawer, setShowingDrawer] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [showingSelect, setShowingSelect] = useState(false);
    const [showingAddressSelect, setShowingAddressSelect] = useState(false);
    const [showingCheckoutModal, setShowingCheckoutModal] = useState(false);
    const [selectingLocation, setSelectingLocation] = useState(false);
    const [selectedCardId, setSelectedCardId] = useState(0);
    const [selectedDeliveryType, setSelectedDeliveryType] = useState(0);
    const [selectedPharmacy, setSelectedPharmacy] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [showCheckoutSection, setShowCheckoutSection] = useState(false);
    const [isClearingPrescription, setIsClearingPrescription] = useState(false);
    const orderFlowRef = useRef();
    const [currentScreen, setCurrentScreen] = useState('basket');
    const handleProcessOrder = () => {
        orderFlowRef.current.goToNextScreen();
    };
    const center = { lat: 48.192, lng: 16.3671 };
    const zoom = 12;

    const closeMapAddressSelect = location => {
        if (location) {
            setCustomAddress(location);
        }
        setSelectingLocation(false);
    };

    const handleQuantityChange = async (newQuantity, index) => {
        const product = basketItems[index];
        try {
            setAddingToBasket(prev => ({ ...prev, [product.productIntegrationId]: true }));
            await updateQuantity(product.productIntegrationId, newQuantity);
            if (newQuantity === 0) {
                toast.info(t('component.basket.toast.product_removed'));
            } else {
                toast.success(t('component.basket.toast.quantity_updated', { quantity: newQuantity }));
            }
        } catch (error) {
            toast.error(t('component.basket.toast.update_failed'));
        } finally {
            setAddingToBasket(prev => ({ ...prev, [product.productIntegrationId]: false }));
        }
    };

    const handleDeleteProduct = async (index) => {
        const product = basketItems[index];
        try {
            setAddingToBasket(prev => ({ ...prev, [product.productIntegrationId]: true }));
            const response = await removeFromBasket(product.productIntegrationId);
            if (response && response.status === 'success') {
                toast.info(t('component.basket.toast.product_removed'));
            } else {
                toast.error(t('component.basket.toast.remove_failed'));
            }
        } catch (error) {
            console.error('Error removing product:', error);
            toast.error(t('component.basket.toast.remove_failed'));
        } finally {
            setAddingToBasket(prev => ({ ...prev, [product.productIntegrationId]: false }));
        }
    };

    const openAddressSelection = () => {
        setShowingDrawer(true);
        setShowingAddressSelect(true);
        toast.info(t('component.basket.toast.select_address'));
    };

    const closeAddressSelection = () => {
        setShowingDrawer(false);
        // wait for drawer animation animation to finish
        setTimeout(() => {
            setShowingAddressSelect(false);
        }, 500);
    };

    const openCheckoutModal = async () => {
        const hasHighQuantity = basketItems.some(item => item.quantity > 3);    
        if (hasHighQuantity) {
          setShowForWhomModal(true);
          toast.info(t('component.basket.toast.specify_order'));
          return;
        }
        await fetchPharmaciesAndShowCheckout();
    };

    const [basketVersion, setBasketVersion] = useState(0);
      useEffect(() => {
        localStorage.removeItem('selectedForWhom');
        setBasketVersion(prev => prev + 1);
    }, [basketItems]);

    // Refresh basket on page mount to get latest data
    useEffect(() => {
        const refreshOnMount = async () => {
            if (!isRefreshing) {
                setIsRefreshing(true);
                await forceRefreshBasket();
                setIsRefreshing(false);
            }
        };
        refreshOnMount();
    }, []); // Empty dependency array ensures this runs only on mount

    const fetchPharmaciesAndShowCheckout = async () => {
        try {
          const response = await sendRequest({
            method: 'POST',
            url: `/module/order/pharmacyMatching`,
            data: {
              addressId: 1,
              radius: 30000,
              products: basketItems,
              limit: 5,
              offset: 0
            }
          });
          if (response?.status === 'success') {
              if (response?.data?.pharmacies?.length) {
                setPharmacies(response.data.pharmacies);
                setSelectedPharmacy(response.data.pharmacies[0]); // Set first pharmacy as default
                setShowingDrawer(true);
                setShowingCheckoutModal(true);
              } else {
                toast.error(t('component.basket.toast.no_pharmacies'));
              }
          } else {
            if(response?.message === 'has_active_order') {
                toast.error(t('component.basket.toast.has_active_order'));
            } else if(response?.message === 'No home address found') {
                toast.error(t('component.basket.toast.no_home_address'));
            } else {
                toast.error(t('component.basket.toast.no_pharmacies'));
            }
          }
        } catch (error) {
          console.error("Pharmacy matching error:", error);
          toast.error(t('component.basket.toast.pharmacy_error'));
        }
    };

    const closeCheckoutModal = async () => {
        setShowingDrawer(false);
        // TODO: Update basket
        // const response = await sendRequest({
        // 	method: 'POST',
        // 	url: `/basket/checkout`,
        // 	data: {
        // 		pharmacyId: "1",
        // 		deliveryAdressId: "1",
        // 		paymentMethodId: 1,
        // 		deliveryTime: 1,
        // 		products: [{}],
        // 		tip: 1.2,
        // 		totalPrice: 18
        // 	}
        // })
        // wait for drawer animation animation to finish
        setTimeout(() => {
            setShowingCheckoutModal(false);
        }, 500);
    };

    const onCloseSelect = () => {
        setShowingDrawer(false);

        // wait for drawer animation animation to finish
        setTimeout(() => {
            setShowingSelect(false);
        }, 500);
    };

    // Add handleCardSelection function
    const handleCardSelection = async (cardId) => {
        // Prevent unnecessary updates if same card is selected or if already refreshing
        if (selectedCardId === cardId || isRefreshing) {
            return;
        }

        setSelectedCardId(cardId);
        setSelectedDeliveryType(cardId); // Set delivery type when card is selected

        // Refresh basket with new order type to get updated fees
        setIsRefreshing(true);
        try {
            await getBasketForOrderType(cardId);
        } finally {
            setIsRefreshing(false);
        }
    };

    const isIPhone = /iPhone/i.test(navigator.userAgent);

    const footerContent = basketItems && basketItems.length > 0 ? (
        <div className='p-4 shadow-top bg-white' style={isIPhone ? { transform: 'translateY(23px)' } : {}}>
            {showCheckoutSection && <BringzzCheckoutPicker onCardSelect={handleCardSelection} />}
            <div className='w-full'>
                {!showCheckoutSection ? (
                    <BringzzButton
                        className='bg-magic-lilac rounded-full flex justify-center font-semibold w-full'
                        onClick={() => setShowCheckoutSection(true)}
                        disabled={basketItems.length === 0 || loading}
                    >
                        {t('component.basket.continue', 'Continue')}
                    </BringzzButton>
                ) : (
                    <BringzzButton
                        className='bg-magic-lilac rounded-full flex justify-center font-semibold w-full'
                        onClick={openCheckoutModal}
                        disabled={basketItems.length === 0 || loading}
                    >
                        {!loading ? t('component.basket.checkout.button') : t('component.basket.checkout.loading')}
                    </BringzzButton>
                )}
            </div>
        </div>
    ) : null;

    return (
        <BringzzPageContainer footer={footerContent} footerHover={true}>
            {selectingLocation ? (
               <p>hh</p>
            ) : (
                <>
                    {/* {showingSelect && (
						<BringzzBottomDrawer
							isOpen={showingDrawer}
							close={onCloseSelect}
							title='Select...'
						>
								<div className='flex items-center relative gap-x-4 justify-center'>
									<div className='h-12 absolute top-1/2 right-0 w-full border-y -translate-y-1/2'></div>
									<div>
										<BringzzRotatingSelect
											options={options}
											onSelect={handleChange}
											onClose={onCloseSelect}
										/>
									</div>
								</div>

								<div className='flex justify-center items-center mt-4'>
									<BringzzButton
										className='bg-magic-lilac'
										onClick={onCloseSelect}
									>
										Apply
									</BringzzButton>
								</div>
						</BringzzBottomDrawer>
					)}
					{showingAddressSelect && (
						<BringzzBottomDrawer
							isOpen={showingDrawer}
							close={closeAddressSelection}
							title='Select adress to continue'
						>
							<CheckoutAddressSelect
								close={closeAddressSelection}
								customAddress={customAddress}
								setShowingMapSelect={setSelectingLocation}
							></CheckoutAddressSelect>
						</BringzzBottomDrawer>
					)}*/}
                    {showingAddressSelect && (
                        <BringzzBottomDrawer
                            isOpen={showingDrawer}
                            close={closeAddressSelection}
                            title='Select address to continue'
                        >
                            <AddressSelect
                                close={closeAddressSelection}
                                setAddress={() => {}}
                                onAddressUpdated={async () => {
                                    await updateUserAddresses();
                                }}
                                activeAddress={user?.activeAddress}
                            />
                        </BringzzBottomDrawer>
                    )}
                    {showingCheckoutModal && (
                        <BringzzBottomDrawer
                            isOpen={showingDrawer}
                            close={closeCheckoutModal}
                            title={t('component.basket.checkout.title')}
                        >
                            <CheckoutSummary
                                deliveryType={selectedDeliveryType}
                                close={closeCheckoutModal}
                                gotToNextScreen={handleProcessOrder}
                                pharmacy={selectedPharmacy || pharmacies[0]}
                                pharmacies={pharmacies}
                                onPharmacySelect={setSelectedPharmacy}
                            />
                        </BringzzBottomDrawer>
                    )}

                    <BringzzTopBar
                        sections={[
                            { 
                              name: 'location', 
                              alignment: 'start', 
                              value: userAddress || t('component.basket.address.select'),
                              onClick: () => {
                                setShowingDrawer(true);
                                setShowingAddressSelect(true);
                              }
                            },
                            { name: 'title', alignment: 'center', value: t('component.basket.title') },
                            {
                                name: 'notification',
                                alignment: 'end',
                                value: hasUnreadNotifications,
                                onClick: () => navigate('/notifications')
                            }
                        ]}
                    />
                    {/* <BringzzPromoBanner
                        header={t('component.basket.promo_banner.header')}
                        subHeader={
                            <>
                                Lectus est mi augue amet fames ultrices.{' '}
                                <span className='underline'>{t('component.basket.promo_banner.terms')}</span>
                            </>}>
                    </BringzzPromoBanner> */}

                    <div className='p-4'>
                        <BringzzText tag='h1' className='font-medium'>
                            {t('component.basket.title')}
                        </BringzzText>
                    </div>

                    {prescriptionInfo && (
                        <>
                            <div className='px-4 flex justify-between items-center'>
                                <BringzzText tag='h3' className='font-medium'>
                                    {t('component.basket.information.title')}
                                </BringzzText>
                                {isClearingPrescription ? (
                                    <BringzzIcon
                                        size="16"
                                        icon="IconSpinner"
                                        folder="LineIcons"
                                        className="animate-spin"
                                    />
                                ) : (
                                    <BringzzIcon
                                        icon="IconClose"
                                        folder="LineIcons"
                                        size="16"
                                        className="cursor-pointer"
                                        onClick={async () => {
                                            setIsClearingPrescription(true);
                                            try {
                                                await clearPrescriptionInfo();
                                                toast.info(t('component.basket.toast.prescription_cleared', 'Prescription and related products removed'));
                                            } catch (error) {
                                                console.error('Error clearing prescription:', error);
                                                toast.error(t('component.basket.toast.prescription_clear_error', 'Failed to clear prescription'));
                                            } finally {
                                                setIsClearingPrescription(false);
                                            }
                                        }}
                                    />
                                )}
                            </div>

                            <div className='px-4'>
                                <div className='flex'>
                                    <div className='w-1/2'>
                                        <BringzzText tag='h4' className='font-medium'>{t('component.basket.information.assured_name')}</BringzzText>
                                        <BringzzText tag='h4' className='font-thin text-gray-500'>{prescriptionInfo.name || 'N/A'}</BringzzText>
                                    </div>
                                    <div className='w-1/2'>
                                        <BringzzText tag='h4' className='font-medium'>{t('component.basket.information.svnr')}</BringzzText>
                                        <BringzzText tag='h4' className='font-thin text-gray-500'>{prescriptionInfo.svnr || 'N/A'}</BringzzText>
                                    </div>
                                </div>
                                <hr className='my-2' />
                                <div className='flex'>
                                    <div className='w-1/2'>
                                        <BringzzText tag='h4' className='font-medium'>{t('component.basket.information.rez_id')}</BringzzText>
                                        <BringzzText tag='h4' className='font-thin text-gray-500'>{prescriptionInfo.rezId || 'N/A'}</BringzzText>
                                    </div>
                                    <div className='w-1/2'>
                                        <BringzzText tag='h4' className='font-medium'>E-Med ID</BringzzText>
                                        <BringzzText tag='h4' className='font-thin text-gray-500'>{prescriptionInfo.eMedId || 'N/A'}</BringzzText>
                                    </div>
                                </div>
                                <hr className='my-2' />
                                <div className='flex'>
                                    <div className='w-1/2'>
                                        <BringzzText tag='h4' className='font-medium'>{t('component.basket.information.issue_date')}</BringzzText>
                                        <BringzzText tag='h4' className='font-thin text-gray-500'>{prescriptionInfo.issuedDate || 'N/A'}</BringzzText>
                                    </div>
                                    <div className='w-1/2'>
                                        <BringzzText tag='h4' className='font-medium'>Expiry Date</BringzzText>
                                        <BringzzText tag='h4' className='font-thin text-gray-500'>{prescriptionInfo.expiredDate || 'N/A'}</BringzzText>
                                    </div>
                                </div>
                                {prescriptionInfo.prescriptionFeeExemption !== undefined && (
                                    <>
                                        <hr className='my-2' />
                                        <div className='flex'>
                                            <div className='w-full'>
                                                <BringzzText tag='h4' className='font-medium'>Fee Exemption</BringzzText>
                                                <BringzzText tag='h4' className='font-thin text-gray-500'>
                                                    {prescriptionInfo.prescriptionFeeExemption ? 'Yes' : 'No'}
                                                </BringzzText>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </>
                    )}

                    {basketItems && basketItems.length === 0 && !loading ? (
                        <EmptyBasketState />
                    ) : (
                        <>
                        <div className='p-4'>
                            <BringzzText tag='h3' className='font-medium'>
                                {t('component.basket.products.title')}
                            </BringzzText>
                        </div>
                        <div className='px-4'>
                        {loading ? (
                            <div className='text-center py-4'>
                                <BringzzText>{t('component.basket.loading')}</BringzzText>
                            </div>
                        ) : basketItems && basketItems.length > 0 ? (
                            basketItems.map((product, index) => (
                                <div
                                    key={product.productIntegrationId || index}
                                    className='flex justify-between items-start border-b pb-4 mb-4'
                                >
                                    {/* Left Section: Product Image */}
                                    <div className='w-1/5 cursor-pointer' onClick={() => navigate(`/product/details/${product.productIntegrationId}`)}>
                                        <img
                                            src={product.imgUrl || DefaultProductImage}
                                            alt={product.name || 'Product Image'}
                                            className='w-full h-auto rounded'
                                        />
                                    </div>

                                    {/* Middle Section: Product Details and Quantity Controls */}
                                    <div className='w-3/5 px-2 flex flex-col'>
                                        <BringzzText tag='h4' className='font-semibold text-sm cursor-pointer' onClick={() => navigate(`/product/details/${product.productIntegrationId}`)}>
                                            {product.name || 'Product Name'}
                                        </BringzzText>
                                        <BringzzText className='text-gray-500 text-xs'>
                                            {product.distributorName || t('component.basket.products.manufacturer')}
                                        </BringzzText>
                                        <div className='text-xs mt-1'>
                                            <span>{`${product.packaging} | ${product.packageQuantity}` || ''}</span>
                                        </div>

                                        {/* Quantity Controls */}
                                        <div className='flex items-center mt-2'>
                                            {addingToBasket[product.productIntegrationId] ? (
                                                <BringzzIcon
                                                    size="22"
                                                    icon="IconSpinner"
                                                    folder="LineIcons"
                                                    className="animate-spin"
                                                />
                                            ) : product.quantity > 0 ? (
                                                <button
                                                    className='border border-gray-400 rounded-full px-2'
                                                    onClick={() =>
                                                        handleQuantityChange(
                                                            Math.max(product.quantity - 1, 0),
                                                            index
                                                        )
                                                    }
                                                    disabled={addingToBasket[product.productIntegrationId]}
                                                >
                                                    –
                                                </button>
                                            ) : (
                                                <BringzzIcon
                                                    size="22"
                                                    icon="IconTrashCan"
                                                    folder="LineIcons"
                                                    className="cursor-pointer"
                                                    onClick={() => handleDeleteProduct(index)}
                                                />
                                            )}
                                            <span className='mx-2'>{product.quantity}</span>
                                            <button
                                                className='border border-gray-400 rounded-full px-2'
                                                onClick={() =>
                                                    handleQuantityChange(product.quantity + 1, index)
                                                }
                                                disabled={addingToBasket[product.productIntegrationId]}
                                            >
                                                +
                                            </button>
                                        </div>
                                    </div>

                                    {/* Right Section: Price */}
                                    <div className='w-1/5 text-right flex flex-col items-end'>
                                        <div className='text-lg font-semibold text-black'>
                                            € {product.price ? (product.price * product.quantity).toFixed(2) : '0.00'}
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : null}
                    </div>
                    </>
                    )}

                    {basketItems && basketItems.length > 0 && (
                        <div className='px-4'>
                            <div className='py-4 border-b'>
                                <BringzzButton
                                    className='border border-black flex items-center justify-center gap-2'
                                    icon={{ variant: 'IconPlus', folder: 'LineIcons', size: '16' }}
                                    onClick={() => navigate('/search')}
                                >
                                    <span className='text-sm font-medium'>
                                        {t('component.basket.add_items', 'Add items')}
                                    </span>
                                </BringzzButton>
                            </div>
                        </div>
                    )}

                    {/* Promo code section */}
                    {/* <div className='p-4'>
                        <BringzzAccordion
                            defaultValue={false}
                            title={
                                <BringzzText tag='h4' className='font-bold'>
                                    Promo Code
                                </BringzzText>
                            }
                        >
                            <BringzzButton className='bg-sand'>Promo10</BringzzButton>
                        </BringzzAccordion>
                    </div> */}

                    {basketItems && basketItems.length > 0 ? (<div className='px-4'>
                        <div className='py-4 border-b'>
                            <div className='justify-between flex mb-2'>
                                <BringzzText tag='h4' className='font-regular'>
                                    {t('component.basket.summary.subtotal')}
                                </BringzzText>
                                <BringzzText tag='h4' className='font-regular'>
                                    € {getBasketSubTotal()}
                                </BringzzText>
                            </div>
                            <div className='justify-between flex mb-2'>
                                <BringzzText tag='h4' className='font-regular'>
                                    {t('component.basket.summary.delivery_fee')}
                                </BringzzText>
                                <BringzzText tag='h4' className='font-regular'>
                                    € {getFees()}
                                </BringzzText>
                            </div>
                            <div className='justify-between flex mb-2'>
                                <BringzzText tag='h4' className='font-bold'>
                                    {t('component.basket.summary.total')}
                                </BringzzText>
                                <BringzzText tag='h4' className='font-bold'>
                                    € {(getBasketTotal())}
                                </BringzzText>
                            </div>
                        </div>
                        <div></div>
                    </div>) : null}

                    <BasketRecommendationSection />
                </>
            )}
            {showForWhomModal && (
                <BringzzBottomDrawer
                    isOpen={showForWhomModal}
                    close={() => setShowForWhomModal(false)}
                    title={t('component.basket.checkout.for_whom.title')}
                >
                    <div className="p-4 space-y-4">
                    <div className="flex flex-col items-center text-center space-y-2">
                        <BringzzIcon name="alert-circle" className="text-red-500 text-3xl" />
                        <BringzzText className="font-semibold text-base text-black">
                            {t('component.basket.checkout.for_whom.exceeded_quantity')}
                        </BringzzText>
                        <BringzzText className="text-sm text-gray-500">
                            {t('component.basket.checkout.for_whom.explanation')}
                        </BringzzText>
                    </div>

                    <BringzzButton
                        className="w-full bg-white border border-gray-300 rounded-lg p-4 text-left flex space-x-3 items-start"
                        onClick={() => {
                        localStorage.setItem('selectedForWhom', 'myself');
                        setShowForWhomModal(false);
                        fetchPharmaciesAndShowCheckout();
                        }}
                    >
                        <BringzzIcon name="user" className="text-primary text-xl mt-1" />
                        <div>
                        <p className="font-semibold">{t('component.basket.checkout.for_whom.myself.title')}</p>
                        <p className="text-sm text-gray-500">{t('component.basket.checkout.for_whom.myself.description')}</p>
                        </div>
                    </BringzzButton>

                    <BringzzButton
                        className="w-full bg-white border border-gray-300 rounded-lg p-4 text-left flex space-x-3 items-start"
                        onClick={() => {
                        localStorage.setItem('selectedForWhom', 'others');
                        setShowForWhomModal(false);
                        fetchPharmaciesAndShowCheckout();
                        }}
                    >
                        <BringzzIcon name="users" className="text-primary text-xl mt-1" />
                        <div>
                        <p className="font-semibold">{t('component.basket.checkout.for_whom.others.title')}</p>
                        <p className="text-sm text-gray-500">{t('component.basket.checkout.for_whom.others.description')}</p>
                        </div>
                    </BringzzButton>
                    </div>
                </BringzzBottomDrawer>
            )}
        </BringzzPageContainer>
    );
};

export default BasketPage;